import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, reviewService, bazaarReviewService } from '../services/api';

type SortOption = 'newest' | 'oldest' | 'rating-desc' | 'rating-asc';
type ReviewTab = 'jobs' | 'bazaar';

interface DisplayReview {
  id: string;
  reviewerName: string;
  rating: number;
  comment: string | null;
  createdAt: string;
  subtitle: string;
}

function Stars({ rating }: { rating: number }) {
  return (
    <span className="text-amber-400 text-sm leading-none" aria-label={`${rating} out of 5 stars`}>
      {'★'.repeat(rating)}
      <span className="text-gray-300">{'★'.repeat(5 - rating)}</span>
    </span>
  );
}

export default function MyReviewsPage() {
  const { getToken, account } = useAppAuth();
  const [activeTab, setActiveTab] = useState<ReviewTab>('jobs');
  const [ratingFilter, setRatingFilter] = useState<number | null>(null);
  const [sort, setSort] = useState<SortOption>('newest');

  const { data: jobReviews = [], isLoading: loadingJobs } = useQuery({
    queryKey: ['my-reviews'],
    queryFn: () => reviewService(createApiClient(getToken)).getReceived(),
    enabled: !!account && activeTab === 'jobs',
  });
  const { data: bazaarReviews = [], isLoading: loadingBazaar } = useQuery({
    queryKey: ['my-reviews', 'bazaar'],
    queryFn: () => bazaarReviewService(createApiClient(getToken)).getReceived(),
    enabled: !!account && activeTab === 'bazaar',
  });

  const isLoading = activeTab === 'jobs' ? loadingJobs : loadingBazaar;

  const reviews: DisplayReview[] = useMemo(() => {
    if (activeTab === 'jobs') {
      return jobReviews.map(r => ({
        id: r.reviewId,
        reviewerName: r.reviewerName,
        rating: r.rating,
        comment: r.comment,
        createdAt: r.createdAt,
        subtitle: r.jobTitle ? `${r.jobReferenceNumber ? `${r.jobReferenceNumber} · ` : ''}${r.jobTitle}` : '',
      }));
    }
    return bazaarReviews.map(r => ({
      id: r.reviewId,
      reviewerName: r.reviewerName,
      rating: r.rating,
      comment: r.comment,
      createdAt: r.createdAt,
      subtitle: r.listingTitle,
    }));
  }, [activeTab, jobReviews, bazaarReviews]);

  const averageRating = reviews.length > 0
    ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
    : 0;

  // Count of reviews at each star value, for the filter chips.
  const countsByRating = useMemo(() => {
    const counts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    for (const r of reviews) counts[r.rating] = (counts[r.rating] ?? 0) + 1;
    return counts;
  }, [reviews]);

  const visibleReviews = useMemo(() => {
    const filtered = ratingFilter === null ? reviews : reviews.filter(r => r.rating === ratingFilter);
    const sorted = [...filtered];
    switch (sort) {
      case 'newest':      sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()); break;
      case 'oldest':      sorted.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()); break;
      case 'rating-desc': sorted.sort((a, b) => b.rating - a.rating); break;
      case 'rating-asc':  sorted.sort((a, b) => a.rating - b.rating); break;
    }
    return sorted;
  }, [reviews, ratingFilter, sort]);

  return (
    <div className="page-shell">
      <div className="flex items-center gap-2 mb-1">
        <Star className="w-6 h-6 text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-900">My Reviews</h1>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Every rating and review left for you by customers, carriers, and workshops on completed jobs.
      </p>

      <div className="flex border-b border-gray-200 mb-6">
        {(['jobs', 'bazaar'] as const).map(t => (
          <button
            key={t}
            onClick={() => { setActiveTab(t); setRatingFilter(null); }}
            className={`px-4 py-2 text-sm font-semibold transition-colors ${
              activeTab === t ? 'text-blue-700 border-b-2 border-blue-600 -mb-px' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {t === 'jobs' ? 'Jobs' : 'Bazaar'}
          </button>
        ))}
      </div>

      {!isLoading && reviews.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <span className="text-3xl font-bold text-gray-900">{averageRating.toFixed(1)}</span>
            <div>
              <Stars rating={Math.round(averageRating)} />
              <p className="text-xs text-gray-400 mt-0.5">
                Based on {reviews.length} review{reviews.length === 1 ? '' : 's'}
              </p>
            </div>
          </div>

          {/* Rating filter chips */}
          <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-gray-100">
            <button
              onClick={() => setRatingFilter(null)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                ratingFilter === null
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              All ({reviews.length})
            </button>
            {[5, 4, 3, 2, 1].map(star => (
              <button
                key={star}
                onClick={() => setRatingFilter(prev => prev === star ? null : star)}
                disabled={countsByRating[star] === 0}
                className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  ratingFilter === star
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {star}★ ({countsByRating[star]})
              </button>
            ))}
          </div>
        </div>
      )}

      {!isLoading && reviews.length > 0 && (
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-gray-400">
            Showing {visibleReviews.length} of {reviews.length}
          </p>
          <div className="flex items-center gap-2">
            <label htmlFor="my-reviews-sort" className="text-xs text-gray-500">Sort by</label>
            <select
              id="my-reviews-sort"
              value={sort}
              onChange={e => setSort(e.target.value as SortOption)}
              className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="rating-desc">Rating: High to Low</option>
              <option value="rating-asc">Rating: Low to High</option>
            </select>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full" />
        </div>
      ) : reviews.length === 0 ? (
        <p className="text-sm text-gray-400">You haven't received any reviews yet.</p>
      ) : visibleReviews.length === 0 ? (
        <p className="text-sm text-gray-400">No reviews match the selected filter.</p>
      ) : (
        <div className="space-y-3">
          {visibleReviews.map((r) => (
            <div key={r.id} className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
              <div className="flex items-start justify-between gap-3 mb-1">
                <div className="min-w-0">
                  <span className="font-semibold text-gray-900 text-sm">{r.reviewerName}</span>
                  {r.subtitle && (
                    <span className="text-xs text-gray-400 ml-2">{r.subtitle}</span>
                  )}
                </div>
                <Stars rating={r.rating} />
              </div>
              {r.comment && <p className="text-sm text-gray-600 mt-1">{r.comment}</p>}
              <p className="text-xs text-gray-400 mt-2">
                {new Date(r.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
